import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import Grid from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import { Chip } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

import { Iso6391Locale } from 'yeself-common/typings/model/Model';
import { ElementLocationPath } from 'yeself-common/typings/guide/LayerCommon';
import { ELEMENT_PATHS, NO_PATH } from 'yeself-common/enum/ModelEnum';
import { PATH } from 'yeself-common/ModelConstant';

const LABELS = {
    [ELEMENT_PATHS.XPATH]: (
        <FormattedMessage id="ElementLocation.field.path.xpath" defaultMessage="Element XPath" />
    ),
    [ELEMENT_PATHS.RELATIVE]: (
        <FormattedMessage
            id="ElementLocation.field.cssPath.relative"
            defaultMessage="Element CSS relative path"
        />
    ),
    [ELEMENT_PATHS.XPATH_LIMITED]: (
        <FormattedMessage
            id="ElementLocation.field.path.xpathLimited"
            defaultMessage="XPath Element - limited"
        />
    ),
    [ELEMENT_PATHS.RELATIVE_LIMITED]: (
        <FormattedMessage
            id="ElementLocation.field.cssPath.relativeLimited"
            defaultMessage="Element CSS relative limited path"
        />
    ),
    [ELEMENT_PATHS.EXPRESSED]: (
        <FormattedMessage
            id="ElementLocation.field.cssPath.expressed"
            defaultMessage="Element CSS expressed path"
        />
    ),
    [ELEMENT_PATHS.CLASS_ONLY]: (
        <FormattedMessage
            id="ElementLocation.field.cssPath.classOnly"
            defaultMessage="Element CSS class-only path"
        />
    ),
    [ELEMENT_PATHS.TAG_ONLY]: (
        <FormattedMessage
            id="ElementLocation.field.cssPath.tagOnly"
            defaultMessage="Element CSS tag-only path"
        />
    ),
    [ELEMENT_PATHS.JS_PATH]: (
        <FormattedMessage
            id="ElementLocation.field.path.jsPath"
            defaultMessage="Element JSPath relative"
        />
    ),
    [ELEMENT_PATHS.JS_PATH_NON_RELATIVE]: (
        <FormattedMessage
            id="ElementLocation.field.path.jsPathNonRelative"
            defaultMessage="Element JSPath non-relative"
        />
    ),
};

const label = (id: string, matches?: number) =>
    matches !== undefined ? (
        <Grid container justify="space-between" alignItems="flex-end" wrap="nowrap">
            <Typography noWrap variant="subheading">
                {LABELS[id]}
            </Typography>
            <Chip
                className={matches === 1 ? '' : 'red'}
                label={`${matches} ${matches === 1 ? 'MATCH' : 'MATCHES'}`}
                color="secondary"
                style={{
                    fontWeight: 'bold',
                    backgroundColor: matches === 1 ? undefined : '#d73939',
                }}
            />
        </Grid>
    ) : (
        LABELS[id]
    );

const formatPath = (value) => (value === PATH.NOT_RECORDED_YET || value === NO_PATH ? '' : value);

type PathFieldProps = {
    onChange: (value: ElementLocationPath) => void;
    value: ElementLocationPath;
    locale: Iso6391Locale;
    matches?: number;
};

const PathField = ({
                       onChange,
                       value,
                       locale,
                       matches,
                   }: PathFieldProps): React.ReactElement => {
    const handleOnChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange(
                value.id === ELEMENT_PATHS.XPATH
                    ? { ...value, path: { ...value.path, [locale]: event.target.value } }
                    : { ...value, path: event.target.value }
            );
        },
        [onChange, value, locale]
    );

    const handleOnSwitchChecked = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const isEnabled = event.target.checked;
            onChange({ ...value, enabled: isEnabled });
        },
        [value, onChange]
    );

    return (
        <Grid container alignItems="flex-start">
            <Grid id="yeself-switch-button" item>
                <Switch checked={value.enabled} onChange={handleOnSwitchChecked} color="primary" />
            </Grid>
            <Grid item xs>
                <TextField
                    label={label(value.id, matches)}
                    value={
                        value.id === ELEMENT_PATHS.XPATH
                            ? formatPath(value.path[locale])
                            : formatPath(value.path)
                    }
                    margin="normal"
                    onChange={handleOnChange}
                    disabled={!value.enabled}
                    fullWidth
                    multiline
                    InputLabelProps={{
                        shrink: true,
                        style: {
                            top: '-10px',
                            width: '133%',
                        },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default PathField;
